/**
 * Модальное окно для управления правами и IP whitelist пользователя
 * Дата: 31.01.2026
 * Связан с: api/userPermissions.ts, pages/Users.tsx
 */
import { useState, useEffect } from 'react';
import { Modal, Button, Input } from '@/components/ui';
import { useToast } from '@/hooks/useToast';
import { 
  userPermissionsApi, 
  Permission, 
  UserPermission, 
  UserIPWhitelist 
} from '@/api/userPermissions';
import { 
  Shield, 
  ShieldCheck, 
  ShieldX, 
  Globe, 
  Plus, 
  Trash2, 
  ToggleLeft, 
  ToggleRight,
  Loader2
} from 'lucide-react';

interface UserPermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
}

export function UserPermissionsModal({ isOpen, onClose, userId, userName }: UserPermissionsModalProps) {
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState<'permissions' | 'ip'>('permissions');
  const [loading, setLoading] = useState(false);
  
  // Permissions state
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [rolePermissions, setRolePermissions] = useState<string[]>([]);
  const [overrides, setOverrides] = useState<UserPermission[]>([]);
  
  // IP Whitelist state
  const [ipList, setIpList] = useState<UserIPWhitelist[]>([]);
  const [newIP, setNewIP] = useState('');
  const [newIPDescription, setNewIPDescription] = useState('');

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && userId) {
      loadData();
    }
  }, [isOpen, userId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [permsData, effectiveData, ipData] = await Promise.all([
        userPermissionsApi.listAllPermissions(),
        userPermissionsApi.getEffectivePermissions(userId),
        userPermissionsApi.getUserIPWhitelist(userId),
      ]);
      
      setAllPermissions(permsData);
      setRolePermissions(effectiveData.role_permissions || []);
      setOverrides(effectiveData.user_overrides || []);
      setIpList(ipData);
    } catch (error) {
      showError('Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  };

  // Group permissions by resource
  const groupedPermissions = allPermissions.reduce((acc, perm) => {
    if (!acc[perm.resource]) {
      acc[perm.resource] = [];
    }
    acc[perm.resource].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  // Check permission status
  const getPermissionStatus = (perm: Permission): 'role' | 'granted' | 'revoked' | 'none' => {
    const override = overrides.find(o => o.permission_id === perm.id);
    if (override) {
      return override.granted ? 'granted' : 'revoked';
    }
    const permStr = `${perm.resource}:${perm.action}`;
    return rolePermissions.includes(permStr) ? 'role' : 'none';
  };

  // Toggle permission override
  const togglePermission = async (perm: Permission) => {
    const currentStatus = getPermissionStatus(perm);
    try {
      if (currentStatus === 'role') {
        // Role has it -> revoke
        await userPermissionsApi.setPermissionOverride(userId, perm.id, false, 'Отозвано администратором');
        showSuccess('Право отозвано');
      } else if (currentStatus === 'none') {
        // Role doesn't have it -> grant
        await userPermissionsApi.setPermissionOverride(userId, perm.id, true, 'Добавлено администратором');
        showSuccess('Право добавлено');
      } else if (currentStatus === 'granted' || currentStatus === 'revoked') {
        // Has override -> remove override (return to role)
        await userPermissionsApi.removePermissionOverride(userId, perm.id);
        showSuccess('Вернуто к ролевым правам');
      }
      await loadData();
    } catch (error) {
      showError('Ошибка изменения права');
    }
  };

  // Add IP to whitelist
  const addIP = async () => {
    if (!newIP.trim()) return;
    try {
      await userPermissionsApi.addUserIP(userId, newIP.trim(), newIPDescription || undefined);
      showSuccess('IP добавлен');
      setNewIP('');
      setNewIPDescription('');
      await loadData();
    } catch (error) {
      showError('Не удалось добавить IP (неверный формат или уже существует)');
    }
  };

  // Remove IP
  const removeIP = async (ipId: string) => {
    try {
      await userPermissionsApi.removeUserIP(userId, ipId);
      showSuccess('IP удалён');
      await loadData();
    } catch (error) {
      showError('Ошибка удаления IP');
    }
  };

  // Toggle IP active
  const toggleIPActive = async (ipId: string, currentActive: boolean) => {
    try {
      await userPermissionsApi.toggleUserIP(userId, ipId, !currentActive);
      showSuccess(currentActive ? 'IP отключен' : 'IP включен');
      await loadData();
    } catch (error) {
      showError('Ошибка изменения статуса IP');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Настройки: ${userName}`}
      size="lg"
    >
      <div className="space-y-4">
        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          <button
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'permissions'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
            onClick={() => setActiveTab('permissions')}
          >
            <Shield className="w-4 h-4 inline mr-2" />
            Права доступа
          </button>
          <button
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'ip'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
            onClick={() => setActiveTab('ip')}
          >
            <Globe className="w-4 h-4 inline mr-2" />
            IP ограничения
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
          </div>
        ) : activeTab === 'permissions' ? (
          /* Permissions Tab */
          <div className="space-y-4 max-h-96 overflow-y-auto">
            <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded border border-slate-200">
              Нажмите на право чтобы переопределить. <span className="text-green-600 font-medium">Зелёные</span> = от роли, <span className="text-blue-600 font-medium">Синие</span> = добавлено вручную, <span className="text-red-600 font-medium">Красные</span> = отозвано.
            </p>
            {Object.entries(groupedPermissions).map(([resource, perms]) => (
              <div key={resource} className="border border-slate-200 bg-white rounded-lg p-3 shadow-sm">
                <h4 className="font-medium text-slate-800 mb-2 capitalize">{resource}</h4>
                <div className="flex flex-wrap gap-2">
                  {perms.map(perm => {
                    const status = getPermissionStatus(perm);
                    return (
                      <button
                        key={perm.id}
                        onClick={() => togglePermission(perm)}
                        className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 transition-all border-2 ${
                          status === 'role'
                            ? 'bg-green-100 text-green-700 border-green-500 hover:bg-green-200'
                            : status === 'granted'
                            ? 'bg-blue-100 text-blue-700 border-blue-500 hover:bg-blue-200'
                            : status === 'revoked'
                            ? 'bg-red-100 text-red-700 border-red-500 hover:bg-red-200'
                            : 'bg-gray-100 text-gray-500 border-gray-300 hover:bg-gray-200'
                        }`}
                        title={perm.description || `${perm.resource}:${perm.action}`}
                      >
                        {status === 'role' && <ShieldCheck className="w-3 h-3" />}
                        {status === 'granted' && <ShieldCheck className="w-3 h-3" />}
                        {status === 'revoked' && <ShieldX className="w-3 h-3" />}
                        {perm.action}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* IP Whitelist Tab */
          <div className="space-y-4">
            <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-3">
              <p className="text-sm text-amber-800 font-medium">
                ⚠️ Если добавлен хотя бы один IP, пользователь сможет входить ТОЛЬКО с указанных адресов.
              </p>
            </div>

            {/* Add IP form */}
            <div className="flex gap-2">
              <Input
                placeholder="IP адрес (например 62.112.102.246)"
                value={newIP}
                onChange={(e) => setNewIP(e.target.value)}
                className="flex-1"
              />
              <Input
                placeholder="Описание (опционально)"
                value={newIPDescription}
                onChange={(e) => setNewIPDescription(e.target.value)}
                className="flex-1"
              />
              <Button onClick={addIP} disabled={!newIP.trim()}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* IP List */}
            {ipList.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <Globe className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Нет ограничений по IP</p>
                <p className="text-sm">Пользователь может входить с любого адреса</p>
              </div>
            ) : (
              <div className="space-y-2">
                {ipList.map(ip => (
                  <div
                    key={ip.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      ip.is_active ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Globe className={`w-5 h-5 ${ip.is_active ? 'text-green-600' : 'text-slate-400'}`} />
                      <div>
                        <span className={`font-mono ${ip.is_active ? 'text-slate-800' : 'text-slate-400'}`}>
                          {ip.ip_address}
                        </span>
                        {ip.description && (
                          <span className="text-slate-500 ml-2">— {ip.description}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleIPActive(ip.id, ip.is_active)}
                        className="p-2 hover:bg-slate-100 rounded"
                        title={ip.is_active ? 'Отключить' : 'Включить'}
                      >
                        {ip.is_active ? (
                          <ToggleRight className="w-5 h-5 text-green-600" />
                        ) : (
                          <ToggleLeft className="w-5 h-5 text-slate-400" />
                        )}
                      </button>
                      <button
                        onClick={() => removeIP(ip.id)}
                        className="p-2 hover:bg-red-100 rounded text-red-500"
                        title="Удалить"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t border-slate-200">
          <Button variant="secondary" onClick={onClose}>
            Закрыть
          </Button>
        </div>
      </div>
    </Modal>
  );
}
